<script lang="ts" setup type="text/x-template" id="editorTemplate">
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick, getCurrentInstance } from "vue";
import { Globals } from "@/Globals";
import {
  getKennungValueDisplay,
  getKennungValue,
  getKennungTitle,
  getColumnByKennung,
} from "@/DataHelper";
import {
  globalsBezeichnung,
  globalsKennung,
  checkBegriff,
  begriffBezeichnung,
} from "@/local";
import { showSnackbar } from "@/UIHelper";
import CommonBaseButtons from "@/components/common/BaseButtons.vue";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";

const emit = defineEmits(["back", "save", "delete"]);
let checkDateRange!: ((von: Date, bis: Date, aic: number) => boolean) | undefined;
let IsAllDay = ref<boolean>(false);
let color: any = "success";
let stellVertreter: boolean = false;
let disabled = ref<boolean>(false);
let stellBew: any = false;
let date: any = "";
let bewilligung: boolean = false;
let stellOffen: boolean = false;
let maName = ref<string>("");
let zeitart = ref<string>("");
let maNameTitel = ref<string>("");
let zeitartTitel = ref<string>("");
let b2 = ref<string>("");
let b2Aic: any = "";
let b2Titel = ref<number>(0);
let checkStellvertreter = ref<string>("");
let resturlaub = ref<string>("");
let loading = ref<boolean>(false);
let loader: any = null;
let aicOffen: number = 0;
let aicEig: number = 0;
let saveVisible = ref<boolean>(false);
let deleteVisible = ref<boolean>(false);
let editErlaubt = ref<boolean>(false);
let saveModell = "";
let setKey = ref<number>(0);
let step = ref<number>(0);

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_Planung_Dialog_Vorgesetzter",
  emitBack: () => handleBack(false),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
let stichtag = ref<Date | null>(pageParams.zeitbereich.bis?.addDays(-1) ?? null);
let isSaving = ref<boolean>(false);
onMounted(async () => {
  step.value = 1;

  if (checkBegriff("Web_KorrekturPlanunngen")) {
    // ist dieser Begriff freigeschalten - soll auch VG neue Urlaube erfassen können
    editErlaubt.value = true;
    pageParams.query = "WebPlanungDialogVG_Erfassbar";
    saveVisible.value = true;
    setKey.value += +1;
    deleteVisible.value = true;
  }
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    maNameTitel.value = getKennungTitle("MA", uiData.columns);
    zeitartTitel.value = getKennungTitle("ZA", uiData.columns);
    maName.value = getKennungValueDisplay(row, "MA", uiData.columns);
    zeitart.value = getKennungValueDisplay(row, "ZA", uiData.columns);
    const allDay = getKennungValueDisplay(row, "B_CALC_FIELD", uiData.columns);
    if (allDay === true) {
      IsAllDay.value = true;
    }
    const zuBewilligen = getKennungValueDisplay(row, "GRANTED", uiData.columns);
    if (zuBewilligen === "ja") {
      bewilligung = true;
    }
    const checkBewilligt = getKennungValueDisplay(row, "b1", uiData.columns);
    checkStellvertreter.value = getKennungValueDisplay(row, "SV", uiData.columns);

    if (checkStellvertreter.value) {
      stellVertreter = true;
      b2Titel.value = getKennungTitle("b2", uiData.columns);
      b2.value = getKennungValueDisplay(row, "b2", uiData.columns);
      b2Aic = getKennungValue(row, "b2", uiData.columns);
      const column = getColumnByKennung("b2", null, uiData.columns);
      const combo = column.meta.combo;
      aicEig = column.aicEig;
      aicOffen = combo[0].aic;

      if (b2.value === "bewilligt") {
        stellBew = true;
      } else if (checkBewilligt === "offen") {
        stellOffen = true;
      }
    }
    const self = checkBegriff("Web_VG_Selfservice");
    if (!self) {
      const maAIC = getKennungValue(uiData.data[0], "MA", uiData.columns);
      const userAIC = AUApi.Current.user.aic;
      if (maAIC === userAIC) {
        disabled.value = true;
        saveVisible.value = false;
        setKey.value += +1;
      }
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});

function handleBack(saved: boolean = false) {
  console.log("🔙 DialogFreigabe - handleBack aufgerufen, saved:", saved);
  emit("back", saved);
}

function bewDelete() {
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    emit("delete", uiData.data[0].aic_Bew_pool);
  }
}
function checkValue(evt: any) {
  const value = evt.value;
  const combo = evt.field.meta.combo;
  const textOffen = combo[0].bezeichnung;
  const aktAic = combo[2].aic; // = abgelehnt
  if (value === aktAic) {
    b2Aic = aicOffen;
    b2.value = textOffen;
    const spalte = "e" + aicEig;
    for (const i in uiData.data) {
      if (i === spalte) {
        uiData.data[i] = b2Aic;
      }
    }
  }
}

function checkChanged(val: any) {
  if (!disabled.value) {
    saveVisible.value = true;
    setKey.value += +1;
  }
}
function checkIntersection(skipChanging: boolean = false) {
  if (checkDateRange) {
    const instance = getCurrentInstance();
    if (instance) {
      const dateRange = instance.proxy?.$refs.dateRange as any;
      if (
        dateRange &&
        (skipChanging || dateRange.changing) &&
        dateRange.von &&
        dateRange.bis
      ) {
        const von = dateRange.von;
        let bis = dateRange.bis;
        if (von.valueOf() === bis.valueOf() && dateRange.IsAllDay) {
          bis = bis.addDays(1);
        }
        if (!checkDateRange(von, bis, uiData.data[0].aic_Bew_pool ?? 0)) {
          return false;
        }
      }
    }
  }
  return true;
}
function onSaveData() {
  if (!checkIntersection()) {
    showSnackbar(
      globalsBezeichnung(Globals.Begriff, "Ueberschneidung", "Überschneidung!")
    );
    return;
  }
  const aicAbfAllBez = globalsKennung(Globals.Begriff, "WebPlanungSpeichern");
  const aicAbfAll = aicAbfAllBez.aic;
  let aic: number = pageParams.queryAIC ?? 0;

  if (aicAbfAll) {
    onSave(uiData, pageParams, aicAbfAll, "", undefined, aic)?.then(() => {
      emit("save");
    });
  } else {
    showSnackbar({
      text: "Abfrage nicht gefunden",
      color: "error",
    });
    uiData.showLoading = false;
    return;
  }
}
function timeChanged(d: any) {
  try {
    if (!d) {
      stichtag.value = null;
      return;
    }

    stichtag.value = d;
  } catch (e) {
    console.error("Stichtag ändern fehlgeschlagen:", e);
  }
}
function calcSalden() {
  loading.value = true;

  const modell = "Web_Resturlaub_per_Stichtag";
  let begriff: any;
  if (modell) {
    begriff = globalsKennung(Globals.Begriff, modell);
  }
  const variablen: VarUebergabe[] = [];
  if (stichtag.value) {
    const from =
      typeof stichtag.value === "string"
        ? new Date(stichtag.value)
        : new Date(stichtag.value);
    from.setHours(0, 0, 0, 0);
    const isoWithTZ = (from as any).toISOStringWithTZ
      ? (from as any).toISOStringWithTZ()
      : from.toISOString();

    variablen.push({
      var: "WebStichtag",
      type: "string",
      wert: isoWithTZ,
      art: 1,
      user: AUApi.Current.user!.user,
      perm: false,
    });
  }
  AUApi.Current.postCalc(
    variablen,
    [],
    begriff.aic,
    pageParams.aic,
    pageParams.zeitbereich,
    0,
    0,
    "",
    successCalc,
    begriff.maxB
  );
}
function successCalc(res: any) {
  resturlaub.value = res.data.ergebnis;
  loading.value = false;
  step.value++;
  return;
}
</script>

<template id="editorTemplate">
  <v-card class="pa-0">
    <v-window v-model="step">
      <v-window-item :value="1">
        <v-card
          v-if="!uiData.showLoading"
          class="dvhDialog"
          :loading="uiData.showLoading"
        >
          <v-toolbar
            density="compact"
            class="subtitle-1 dvhBackground lighten-2"
            v-if="uiData.initialized"
            style="color: white"
          >
            <span class="pl-2" style="color: white">
              {{ maName }}:
              {{ zeitart }}
            </span>
            <v-spacer></v-spacer>

            <v-divider class="px-2" vertical></v-divider>

            <v-btn @click="handleBack(false)" icon="mdi-close"> </v-btn>
          </v-toolbar>
          <v-card-text class="pb-0" v-if="uiData.initialized">
            <EditComboBox
              kennung="ZA"
              id="EventType"
              :name="getKennungTitle('Zeitart', uiData.columns)"
              style="width: 100%"
              v-if="editErlaubt"
            />

            <EditDateRange2
              ref="dateRange"
              kennung="vb"
              :IsAllDay="IsAllDay"
              :disabled="false"
              :showGanztag="true"
              v-if="editErlaubt"
            ></EditDateRange2>

            <EditDateRange
              ref="dateRange"
              kennung="vb"
              :IsAllDay="IsAllDay"
              :disabled="true"
              v-if="!editErlaubt"
            ></EditDateRange>

            <EditTextArea
              id="Description"
              kennung="MEMO"
              name="Bemerkung"
              :disabledMounted="true"
              class="mt-5"
            />
            <div style="text-align: left" v-if="checkStellvertreter">
              {{ b2Titel }}: {{ b2 }} von {{ checkStellvertreter }}
            </div>
            <EditCheckBox3
              @seteditvalue="checkValue"
              :readonly="disabled"
              @change="checkChanged"
              kennung="b1"
            />

            <EditTextArea
              id="VG"
              kennung="VG"
              @change="checkChanged"
              name="Bemerkung"
              :disabledMounted="false"
            />
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-spacer></v-spacer>
            <CommonBaseButtons
              :key="setKey"
              :createVisible="false"
              :refreshVisible="false"
              :deleteVisible="deleteVisible"
              @back="handleBack(false)"
              @delete="bewDelete"
              :saveVisible="saveVisible"
              @save="onSaveData"
            ></CommonBaseButtons>
          </v-card-actions>
        </v-card>
      </v-window-item>
      <v-window-item :value="2">
        <v-card class="dvhDialog">
          <v-card-title>
            {{
              globalsBezeichnung(
                Globals.Begriff,
                "Web_Berechnung_Resturlaub",
                "Berechnung Resturlaub per Stichtag"
              )
            }}
          </v-card-title>
          <v-card-text>
            <span style="white-space: pre" v-if="resturlaub">
              {{ begriffBezeichnung("Web_Text_Anspruch") }}
              {{ resturlaub }}
            </span></v-card-text
          ></v-card
        >
      </v-window-item>
    </v-window>
    <v-divider color="warning"></v-divider>

    <v-card-actions class="calc-actions">
      <v-btn
        v-if="step !== 1"
        :disabled="step === 1"
        variant="text"
        @click="step--"
        icon="mdi-backspace"
      >
      </v-btn>

      <div class="d-flex flex-column ml-0">
        <span class="text-caption mb-2">
          {{
            globalsBezeichnung(
              Globals.Begriff,
              "Web_Berechnung_Resturlaub",
              "Berechnung Resturlaub per Stichtag"
            )
          }}
        </span>
        <div class="d-flex align-start calc-row">
          <EditDatePicker
            v-if="step !== 2"
            v-model="stichtag"
            @changeValue="timeChanged"
            class="e-field"
            :placeholder="globalsBezeichnung(Globals.Begriff, 'Web_Stichtag', 'Stichtag')"
            :disableValueChangeTracker="true"
            :title="globalsBezeichnung(Globals.Begriff, 'Web_Stichtag', 'Stichtag')"
            style="min-width: 158px"
            :noMessages="true"
            :description="globalsBezeichnung(Globals.Begriff, 'Web_Stichtag', 'Stichtag')"
          ></EditDatePicker>

          <v-btn
            v-if="step !== 2"
            class="ml-2 calc-btn"
            @click="calcSalden()"
            :disabled="step === 2"
            color="primary"
            variant="tonal"
            :loading="loading"
            prepend-icon="mdi-play-circle"
            min-width="240px"
            height="50"
            min-height="50"
          >
            {{ globalsBezeichnung(Globals.Begriff, "Web_Text_Anspruch", "Resturlaub") }}
            <template v-slot:loader>
              <span class="custom-loader">
                <v-icon light>mdi-cached</v-icon>
              </span>
            </template>
          </v-btn>
        </div>
      </div>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.fullcol {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
  margin: 0px;
}
.dvhDialog {
  padding: 0px;
}
@media (max-width: 600px) {
  .calc-actions {
    width: 90%;
    padding: 5px;
    margin: 5px;
  }
  .calc-row {
    width: 100%;
  }
  .calc-btn {
    margin-right: 0px !important;
  }
}
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
